import { useApolloClient } from '@apollo/react-hooks'
import { gql } from 'apollo-boost'
import React from 'react'
import {
  MessageAppearanceAcksByMessageAppearanceIdsV2,
  MessageAppearanceAcksByMessageAppearanceIdsV2Variables
} from './__generated__/MessageAppearanceAcksByMessageAppearanceIdsV2'
import { getAckTimestampForDelay } from '../ackTimestamp'

export const MESSAGE_APPEARANCE_ACKS_BY_MESSAGE_APPEARANCE_IDS_V2_QUERY = gql`
  query MessageAppearanceAcksByMessageAppearanceIdsV2($messageAppearanceIds: [String!]!) {
    messageAppearanceAcksByMessageAppearanceIdsV2(messageAppearanceIds: $messageAppearanceIds) {
      messageAppearanceId
      clientTimestamp
      serverTimestamp
    }
  }
`

const ACK_BATCH_SIZE = 25

const chunkIds = (ids: string[], size: number) => {
  const chunks: string[][] = []
  for (let i = 0; i < ids.length; i += size) {
    chunks.push(ids.slice(i, i + size))
  }
  return chunks
}

const mapsEqual = (left: Map<string, number | null>, right: Map<string, number | null>) => {
  if (left.size !== right.size) return false
  let equal = true
  left.forEach((value, key) => {
    if (right.get(key) !== value) equal = false
  })
  return equal
}

export const useMessageAppearanceAcksByMessageAppearanceIdsV2 = (
  messageAppearanceIds: string[],
  { skip }: { skip?: boolean } = {}
) => {
  const client = useApolloClient()
  const stableIds = React.useMemo(
    () => Array.from(new Set(messageAppearanceIds)).sort(),
    [messageAppearanceIds]
  )
  const idsKey = stableIds.join('\0')
  const [mergedById, setMergedById] = React.useState(() => new Map<string, number | null>())
  const mergedByIdRef = React.useRef(mergedById)
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<Error | undefined>(undefined)

  React.useEffect(() => {
    mergedByIdRef.current = mergedById
  }, [mergedById])

  React.useEffect(() => {
    if (stableIds.length === 0) {
      setMergedById(new Map())
      setLoading(false)
      setError(undefined)
      return
    }

    if (skip) {
      setLoading(false)
      return
    }

    const idsToFetch = stableIds.filter(id => !mergedByIdRef.current.has(id))
    const prunedById = new Map<string, number | null>()
    for (const id of stableIds) {
      if (mergedByIdRef.current.has(id)) {
        prunedById.set(id, mergedByIdRef.current.get(id) ?? null)
      }
    }

    if (idsToFetch.length === 0) {
      setMergedById(prev => (mapsEqual(prev, prunedById) ? prev : prunedById))
      setLoading(false)
      setError(undefined)
      return
    }

    let alive = true
    setLoading(true)
    setError(undefined)

    const load = async () => {
      const next = new Map(prunedById)
      try {
        for (const chunk of chunkIds(idsToFetch, ACK_BATCH_SIZE)) {
          const result = await client.query<
            MessageAppearanceAcksByMessageAppearanceIdsV2,
            MessageAppearanceAcksByMessageAppearanceIdsV2Variables
          >({
            query: MESSAGE_APPEARANCE_ACKS_BY_MESSAGE_APPEARANCE_IDS_V2_QUERY,
            variables: { messageAppearanceIds: chunk },
            fetchPolicy: 'network-only'
          })

          const rows = result.data?.messageAppearanceAcksByMessageAppearanceIdsV2 ?? []
          const ackById = new Map<string, number>()
          for (const row of rows) {
            if (row?.messageAppearanceId != null && row.clientTimestamp != null) {
              ackById.set(row.messageAppearanceId, getAckTimestampForDelay(row))
            }
          }

          for (const id of chunk) {
            next.set(id, ackById.get(id) ?? null)
          }
        }

        if (!alive) return
        setMergedById(next)
        setError(undefined)
      } catch (err) {
        if (!alive) return
        setError(err instanceof Error ? err : new Error('Failed to load message appearance acks'))
      } finally {
        if (alive) {
          setLoading(false)
        }
      }
    }

    void load()

    return () => {
      alive = false
    }
  }, [client, idsKey, skip, stableIds])

  const data = React.useMemo((): MessageAppearanceAcksByMessageAppearanceIdsV2 | undefined => {
    if (stableIds.length === 0) {
      return undefined
    }
    return {
      messageAppearanceAcksByMessageAppearanceIdsV2: stableIds.map(id => {
        if (!mergedById.has(id)) {
          return null
        }
        const clientTimestamp = mergedById.get(id)
        if (clientTimestamp == null) {
          return null
        }
        return {
          __typename: 'MessageAppearanceAckV2' as const,
          messageAppearanceId: id,
          clientTimestamp,
          serverTimestamp: null
        }
      })
    }
  }, [stableIds, mergedById])

  return {
    data,
    loading,
    error
  }
}
